import $ from 'jquery';
import $script from 'scriptjs';
import layout from '../Layout';
import ProductQuickshop from './ProductQuickshop';

import {trapFocus, removeTrapFocus} from '../helpers/a11y';
import AddToCartFlyout from '../components/AddToCartFlyout';

export default class ProductGridItem {
  constructor($el) {
    this.$el = $el;
    this.productQuickshop = null;
    this.quickBuySettings = null;
    this.events = [];

    this.$window = $(window);
    this.$html = $('html');
    this.$content = this.$el.find('[data-product-item-content]');
    this.$actions = this.$el.find('[data-product-actions]');
    this.hasProductActions = this.$actions.length;

    this._addToCart = this._addToCart.bind(this);
    this._actionsToggle = this._actionsToggle.bind(this);
    this._openQuickShop = this._openQuickShop.bind(this);

    this._onATCOpen = this._onATCOpen.bind(this);
    this._onATCClose = this._onATCClose.bind(this);

    if (this.hasProductActions) {
      if (this.$html.hasClass('no-touch')) {
        this.events.push(this.$el.on('mouseenter.product-item', this._actionsToggle));
        this.events.push(this.$el.on('mouseleave.product-item', this._actionsToggle));
      }

      //$scripts checks existence of script in header before attempting to inject
      $script($('[data-scripts]').data('shopify-api-url'), () => {
        this.events.push(this.$el.on('click.product-item', '[data-quick-buy]', this._addToCart));
        this.events.push(this.$el.on('click.product-item', '[data-quickshop-slim]', this._openQuickShop));
        this.events.push(this.$el.on('click.product-item', '[data-quickshop-full]', this._openQuickShop));
      });
    }

    if (this.$el.find('[data-quick-buy]').length) {
      this._initQuickBuy();
    }

    this._objectFitPolyfill();
  }

  _initQuickBuy() {
    try {
      this.quickBuySettings = JSON.parse(this.$el.find('[data-quick-buy-settings]').text());
    } catch (error) {
      return console.warn(`Quick buy: invalid QuickBuy data found. ${error.message}`);
    }
  }

  _openQuickShop(event) {
    event.preventDefault();

    const modalClass = event.currentTarget.hasAttribute('data-quickshop-full')
      ? 'quickshop-full'
      : 'quickshop-slim';

    if (this.productQuickshop) {
      this.productQuickshop.unload();
    }

    this.productQuickshop = new ProductQuickshop({
      $el: this.$el,
      modalClass,
    });
  }

  _isObjectFitAvailable() {
    return 'objectFit' in document.documentElement.style;
  }

  _objectFitPolyfill() {
    if (this._isObjectFitAvailable()) {
      return;
    }

    const $image = $('[data-product-item-image]', this.$el);
    const src = $('img', $image).attr('src');

    $image.addClass('product-item-image-no-objectfit');
    $image.css('background-image', `url("${ src }")`);
  }

  /**
   * Get height of element, and combined height of element + actions
   * 
   * @returns {{heightBase, heightExpanded: *}}
   * @private
   */
  _getHeights() {
    const height = this.$el.height();
    const actionsHeight = this.$el.find('[data-product-actions]').outerHeight(true);

    return {
      heightBase: height,
      heightExpanded : height + actionsHeight,
    };
  }

  _actionsToggle(event) {
    if (!layout.isBreakpoint('L')) return;

    const open = event.type === 'mouseenter';

    if (open) {
      this._showActions();
    }

    if (!open) {
      this._hideActions();
    }
  }

  _showActions() {
    if (this.$el.revealer('isVisible')) {
      return this._hideActions();
    }

    const { heightBase, heightExpanded } = this._getHeights();

    // Lock heights to prevent jumping
    this.$el.css('height', heightBase);
    this.$content.css('height', heightBase);

    // Store height for when hovering out
    this.$el.data('base-height', heightBase);

    // Start animation, and transition height to expanded height
    this.$el
      .revealer('show')
      .one('revealer-animating.product-item', () => {
        this.$content.css('height', heightExpanded);
      });
  }

  _hideActions() {
    const height = this.$el.data('base-height');

    /*
      - Transition buttons up, using base height for animation
      - Remove heights after animation is complete in case of resize
     */
    this.$el
      .revealer('hide')
      .one('revealer-animating', () => {
        this.$el.off('revealer-animating.product-item');
        this.$el.css({ height });
        this.$content.css({ height });
      }).one('revealer-hide', () => {
        this.$el
          .off('revealer-hide.product-item')
          .css('height', '');
        this.$content.css('height', '');
      });
  }

  _addToCart(event) {
    event.preventDefault();
    const $atcButton = $(event.currentTarget);
    const variantID = $atcButton.attr('data-variant-id');

    const formData = [
      {
        'name': 'id',
        'value': variantID,
      },
      {
        'name': 'quantity',
        'value': 1,
      },
    ];

    const options = {
      $atcButton,
      settings: {
        moneyFormat: this.quickBuySettings.money_format,
        cartRedirection: this.quickBuySettings.cart_redirection,
      },
    };

    const callbacks = {
      onOpen: this._onATCOpen,
      onClose: this._onATCClose,
    };

    AddToCartFlyout.init(formData, options, callbacks);
  }

  /**
   * Callback when AddToCartFlyout opens
   * Move keyboard focus into flyout
   *
   * @param {object} options - Options to be used
   * @param {jQuery} options.$container - Container to trap focus within
   * @param {jQuery} options.$elementToFocus - Element to be focused when focus leaves container
   * @private
   */
  _onATCOpen(options) {
    trapFocus({
      $container: options.$container,
      $elementToFocus: options.$elementToFocus,
      namespace: 'product-item-flyout',
    });
  }

  /**
   * Callback when AddToCartFlyout closes
   * Move keyboard focus back into grid item
   *
   * @private
   */
  _onATCClose() {
    removeTrapFocus({
      namespace: 'product-item-flyout',
    });

    this.$el.focus();
  }

  unload() {
    this.events.forEach($el => $el.off('.product-item'));

    if (this.productQuickshop) {
      this.productQuickshop.unload();
    }
  }
}
